import React, { useState } from "react";
import { AlertCircle, CheckCircle2, Info, KeyRound, Loader2, Pencil, Plus, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button, EmptyState, Loading, Modal, StatCard } from "../../components/ui";
import { apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { AdminAccount, ClubRow } from "../../data/types";
import ExpiryPill from "../../components/ExpiryPill";

/*
 * Club logins.
 *
 * One login per club, created here by the Association. Whoever at the club uses
 * it is the club's own business — the account represents the club, not a person.
 *
 * Each carries an expiry, because clubs subscribe. Past it the login still works
 * and still sees everything; it simply cannot upload until renewed. Locking a
 * club out entirely would also hide the notice telling them to renew.
 */
export default function AdminUsers() {
  const { data: users, loading, reload } = useApi<AdminAccount[]>("/admin/users", []);
  const { data: clubs } = useApi<ClubRow[]>("/admin/clubs", []);
  const [editing, setEditing] = useState<AdminAccount | null>(null);
  const [pending, setPending] = useState<AdminAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  const clubLogins = users.filter((u) => !u.isMaster);
  // Code -> full name, so a login can be read without knowing the abbreviations.
  const clubName = (code?: string | null) => clubs.find((c) => c.name === code)?.fullName || "";
  // The last Association login is the way back into the portal, so it is the
  // only one that cannot be deleted.
  const masters = users.filter((u) => u.isMaster).length;
  const expiringSoon = clubLogins.filter((u) => u.expiry?.warn !== null && u.expiry?.warn !== undefined && !u.expiry?.expired).length;
  const expired = clubLogins.filter((u) => u.expiry?.expired).length;

  const remove = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const res = await apiDelete<{ message: string }>(`/admin/users/${encodeURIComponent(pending.username)}`);
      setNotice({ tone: "ok", text: res.message });
      setPending(null);
      reload();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <AdminTopbar
        title="System Users"
        subtitle="Association administrators and club logins. A club login uploads and manages only its own club's data."
        actions={
          <Button onClick={() => setEditing({ username: "", club: "", name: "", email: "", active: true } as AdminAccount)}>
            <Plus size={16} /> Add Login
          </Button>
        }
      />

      {/*
        The upload rule, stated on the page that sets it up. It lived in the
        code and in conversation only, which is exactly how a rule gets
        forgotten and then argued about six months later.
      */}
      <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-line bg-slate-50 px-4 py-3 text-[13px] text-slate-600">
        <Info size={16} className="mt-px shrink-0 text-primary" />
        <span>
          <b className="text-ink">Who can upload what.</b> A club login may upload only workbooks whose{" "}
          <b>Host club</b> on the Event sheet matches the <b>Club</b> on its login — so a Hornsby login cannot
          upload a Katoomba shoot, whoever asked them to. The username itself carries no meaning and can be
          anything (<code>KRC1</code>, <code>ABC123</code>); it is the Club field beside it that grants the access.
          <br />
          Past its <b>Subscription expires</b> date a club can still sign in and read, but not upload.
          <br />
          <b className="text-ink">The Association's own admin login is exempt</b> — it can upload for any club, at any
          time, expired or not.
        </span>
      </div>

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<UserRound size={20} />} value={clubLogins.length} label="Club Logins" />
        <StatCard icon={<ShieldCheck size={20} />} value={users.filter((u) => u.isMaster).length} label="Association Logins" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<AlertCircle size={20} />} value={expiringSoon} label="Expiring Soon" iconBg={expiringSoon ? "bg-warning-soft text-warning" : "bg-slate-100 text-muted"} />
        <StatCard icon={<AlertCircle size={20} />} value={expired} label="Expired" iconBg={expired ? "bg-danger-soft text-danger" : "bg-slate-100 text-muted"} />
      </div>

      <div className="card mt-3">
        <div className="table-scroll [--table-offset:250px]">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">Login</th>
                <th className="th">Club</th>
                <th className="th">Role</th>
                <th className="th">Expires</th>
                <th className="th">Status</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading && <tr><td colSpan={6} className="td py-10 text-center"><Loading /></td></tr>}
              {!loading && !users.length && (
                <tr>
                  <td colSpan={6} className="td py-12 text-center">
                    <EmptyState title="No logins yet" message="Add a login for each club that will upload results." />
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.username} className="transition-colors hover:bg-primary-soft/30">
                  <td className="td">
                    <div className="font-bold text-ink">{u.username}</div>
                    <div className="text-xs text-muted">{u.email || u.name || "—"}</div>
                  </td>
                  <td className="td">
                    <div className="font-semibold">{u.club || "—"}</div>
                    {clubName(u.club) && <div className="text-xs text-muted">{clubName(u.club)}</div>}
                  </td>
                  <td className="td">
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${u.isMaster ? "bg-violet-50 text-violet-600" : "bg-primary-soft text-primary"}`}>
                      {u.isMaster ? "Association" : "Club"}
                    </span>
                  </td>
                  <td className="td"><ExpiryPill expiry={u.expiry} /></td>
                  <td className="td">
                    {u.active === false ? (
                      <span className="rounded-md bg-danger-soft px-2 py-0.5 text-[11px] font-bold text-danger">Disabled</span>
                    ) : (
                      <span className="rounded-md bg-success-soft px-2 py-0.5 text-[11px] font-bold text-success">Active</span>
                    )}
                  </td>
                  <td className="td text-right">
                    <span className="inline-flex gap-1.5">
                      <button
                        className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50"
                        aria-label={`Edit ${u.username}`}
                        onClick={() => setEditing(u)}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="rounded-lg border border-danger/40 p-2 text-danger transition-colors hover:bg-danger-soft disabled:opacity-40"
                        aria-label={`Remove ${u.username}`}
                        title={
                          u.isMaster && masters <= 1
                            ? "The only Association login cannot be removed"
                            : "Remove this login"
                        }
                        disabled={u.isMaster && masters <= 1}
                        onClick={() => setPending(u)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserEditor
        user={editing}
        clubs={clubs}
        onClose={() => setEditing(null)}
        onSaved={(msg) => {
          setEditing(null);
          setNotice({ tone: "ok", text: msg });
          reload();
        }}
      />

      <ConfirmDialog
        open={!!pending}
        busy={busy}
        title="Remove this login?"
        intro={`${pending?.club || pending?.username} will no longer be able to sign in or upload.`}
        items={pending ? [`${pending.username}${pending.club ? ` — ${pending.club}` : ""}`] : []}
        note={<>Nothing that club has already uploaded is affected — results, competitors and rankings all stay exactly as they are.</>}
        confirmLabel="Remove login"
        onConfirm={remove}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function UserEditor({
  user,
  clubs,
  onClose,
  onSaved,
}: {
  user: AdminAccount | null;
  clubs: ClubRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [form, setForm] = useState<Partial<AdminAccount> & { password?: string }>({});
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user && loadedFor !== user.username) {
    setLoadedFor(user.username);
    setForm({ ...user, password: "" });
    setError(null);
  }
  if (!user) {
    if (loadedFor !== null) setLoadedFor(null);
    return null;
  }

  const isNew = !user.username;
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  /*
   * Which kind of login this is. An existing one cannot change kind — that
   * would silently hand a club the run of the portal, or strip the Association
   * of it — so the choice is offered only when creating.
   */
  const master = isNew ? form.accountRole === "master" : !!user.isMaster;

  const save = async () => {
    setError(null);
    if (isNew && !String(form.username || "").trim()) return setError("Choose a username for the club to sign in with.");
    if (isNew && String(form.password || "").length < 6) return setError("Set a password of at least 6 characters.");
    if (!master && !String(form.club || "").trim()) return setError("Choose the club this login belongs to.");

    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        username: form.username,
        name: form.name,
        email: form.email,
        club: form.club,
        expiresAt: form.expiresAt || "",
        active: form.active !== false,
        // Only meaningful when creating; the backend ignores it on an edit.
        accountRole: master ? "master" : "club",
      };
      if (form.password) body.password = form.password;
      const res = isNew
        ? await apiPost<{ message: string }>("/admin/users", body)
        : await apiPut<{ message: string }>(`/admin/users/${encodeURIComponent(user.username)}`, body);
      onSaved(res.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? (master ? "Add Association administrator" : "Add club login") : `Edit ${user.username}`}
    >
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] font-semibold text-danger">
            <AlertCircle size={15} className="mt-px shrink-0" /> {error}
          </div>
        )}

        {/*
          What this login is allowed to do. The Association runs the service and
          needs more than one person able to do so — a results portal that only
          one administrator can maintain stops the week they go away.
        */}
        {isNew && (
          <div>
            <label className="label">Login type</label>
            <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                { value: "club", title: "Club login", note: "Uploads its own club's shoots, while its subscription is current." },
                { value: "master", title: "Association administrator", note: "Uploads for any club, never expires, can manage logins." },
              ].map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => set("accountRole", o.value)}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    (form.accountRole ?? "club") === o.value
                      ? "border-primary bg-primary-soft"
                      : "border-line bg-white hover:bg-slate-50"
                  }`}
                >
                  <span className="block text-[13px] font-bold text-ink">{o.title}</span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-muted">{o.note}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Username</label>
            <input
              className="input"
              placeholder="krc"
              value={form.username ?? ""}
              disabled={!isNew}
              onChange={(e) => set("username", e.target.value)}
            />
            {!isNew && <p className="mt-1 text-xs text-muted">A username cannot be changed once created.</p>}
          </div>
          <div>
            <label className="label">Club</label>
            {/*
              A select rather than free text: the code alone ("HRSLRC") is not
              something anyone can be expected to recall, and a typo here would
              create a login that matches no workbook and fails only at upload.
            */}
            <select
              className="input appearance-none pr-8"
              value={form.club ?? ""}
              disabled={master}
              onChange={(e) => set("club", e.target.value)}
            >
              <option value="">{master ? "All clubs" : "Choose a club..."}</option>
              {clubs.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.fullName ? `${c.name} — ${c.fullName}` : c.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted">
              {master
                ? "An Association login belongs to no club, which is what lets it upload for all of them."
                : 'Must match "Host club" on their workbook exactly — this is what grants the access, not the username.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Contact name</label>
            <input className="input" value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="label">Contact email</label>
            <input className="input" value={form.email ?? ""} onChange={(e) => set("email", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">{isNew ? "Password" : "New password"}</label>
            <input
              className="input"
              type="text"
              placeholder={isNew ? "At least 6 characters" : "Leave blank to keep the current one"}
              value={form.password ?? ""}
              onChange={(e) => set("password", e.target.value)}
            />
            <p className="mt-1 flex items-center gap-1 text-xs text-muted"><KeyRound size={11} /> Give this to the club.</p>
          </div>
          <div>
            <label className="label">{master ? "Subscription expires — not applicable" : "Subscription expires"}</label>
            <input
              className="input"
              type="date"
              value={String(form.expiresAt ?? "").slice(0, 10)}
              onChange={(e) => set("expiresAt", e.target.value)}
            />
            <p className="mt-1 text-xs text-muted">Leave blank for no expiry. After this date they can view but not upload.</p>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-ink">
          <input type="checkbox" checked={form.active !== false} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-primary" />
          Account enabled
          <span className="font-normal text-muted">— unticking disables this account from signing in</span>
        </label>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save login
          </Button>
        </div>
      </div>
    </Modal>
  );
}
